import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { messagesAPI, requestsAPI } from '../lib/api';
import { formatRelativeTime, getServiceName, formatDateTime } from '../lib/constants';
import { ArrowLeft, PaperPlaneTilt, Dog } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function ChatPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [requestInfo, setRequestInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await messagesAPI.getMessages(requestId);
      setMessages(data);
    } catch (error) {
      console.error('Fetch messages error:', error);
    }
  }, [requestId]);

  const fetchData = useCallback(async () => {
    try {
      const [messagesData, requestsData] = await Promise.all([
        messagesAPI.getMessages(requestId),
        requestsAPI.getRequests()
      ]);
      
      setMessages(messagesData);
      const req = requestsData.find(r => r.request_id === requestId);
      setRequestInfo(req);
    } catch (error) {
      toast.error('Erro ao carregar conversa');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchData, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await messagesAPI.sendMessage(requestId, newMessage.trim());
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getOtherParty = () => {
    if (!requestInfo) return null;
    const isProvider = user?.role === 'prestador';
    return isProvider ? requestInfo.client : requestInfo.provider?.user;
  };

  const otherParty = getOtherParty();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      {/* Chat Header */}
      <div className="border-b bg-card sticky top-16 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Avatar>
              <AvatarImage src={otherParty?.picture} />
              <AvatarFallback>{otherParty?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold truncate">{otherParty?.name || 'Chat'}</h2>
              {requestInfo && (
                <p className="text-xs text-muted-foreground truncate">
                  {getServiceName(requestInfo.service_type)} • {formatDateTime(requestInfo.start_date)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="container mx-auto max-w-2xl space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Dog weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Comece a conversa!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isSent = msg.sender_id === user?.user_id;
              return (
                <motion.div
                  key={msg.message_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`chat-bubble ${isSent ? 'sent' : 'received'} p-3`}>
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isSent ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {formatRelativeTime(msg.created_at)}
                    </p>
                  </div>
                </motion.div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t bg-card sticky bottom-0">
        <div className="container mx-auto max-w-2xl p-4">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escreva uma mensagem..."
              className="flex-1 rounded-full"
              disabled={sending}
              data-testid="message-input"
            />
            <Button 
              onClick={handleSend}
              disabled={sending || !newMessage.trim()}
              className="rounded-full"
              data-testid="send-btn"
            >
              <PaperPlaneTilt className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
